import { ReactNode, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Sidebar from '@/components/Sidebar'
import ArticleTypeModal from '@/components/ArticleTypeModal'

interface AdminLayoutProps {
  children: ReactNode
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const [isArticleModalOpen, setArticleModalOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  const hideHeader = location.pathname.includes('/admin/content/web-page/')

  const getPageTitle = () => {
    const pathname = location.pathname

    // Handle nested routes
    if (pathname.includes('/press-release')) {
      if (pathname.includes('/new')) return 'New Press Release'
      if (pathname.includes('/edit')) return 'Edit Press Release'
      if (pathname.match(/\/press-release\/[^/]+$/)) return 'Press Release Details'
      return 'Press Release'
    }
    if (pathname.includes('/strategic-pillars')) {
      if (pathname.includes('/new')) return 'New Strategic Pillar'
      if (pathname.includes('/edit')) return 'Edit Strategic Pillar'
      if (pathname.match(/\/strategic-pillars\/[^/]+$/)) return 'Strategic Pillar Details'
      return 'Strategic Pillars'
    }
    if (pathname.includes('/programmes')) {
      if (pathname.includes('/new')) return 'New Programme'
      if (pathname.includes('/edit')) return 'Edit Programme'
      if (pathname.match(/\/programmes\/[^/]+$/)) return 'Programme Details'
      return 'Programmes'
    }
    if (pathname.includes('/publications')) {
      if (pathname.includes('/new')) return 'New Publication'
      if (pathname.includes('/edit')) return 'Edit Publication'
      if (pathname.match(/\/publications\/[^/]+$/)) return 'Publication Details'
      return 'Publications'
    }
    if (pathname.includes('/legal-foundations')) {
      if (pathname.includes('/new')) return 'New Legal Foundation'
      if (pathname.includes('/edit')) return 'Edit Legal Foundation'
      if (pathname.match(/\/legal-foundations\/[^/]+$/)) return 'Legal Foundation Details'
      return 'Legal Foundations'
    }
    if (pathname.includes('/photo-gallery')) {
      if (pathname.includes('/upload')) return 'Upload Photos'
      return 'Photo Gallery'
    }
    if (pathname.includes('/admin-users/')) {
      if (pathname.match(/\/admin-users\/[^/]+$/)) return 'Staff Profile'
      return 'Admin Users'
    }

    // Base routes
    const titleMap: Record<string, string> = {
      // get greeting eg Good morning, afternoon, evening based on time of day
      '/admin/dashboard': `Hi, Good ${getTimeOfDay()}! 👋`,
      '/admin/content': 'Website Pages',
      '/admin/subscribers': 'Subscribers',
      '/admin/admin-users': 'Admin Users',
      '/admin/videos': 'Videos',
      '/admin/about': 'About',
      '/admin/opportunities': 'Opportunities',
      '/admin/documents': 'Procurement Docs',
      '/admin/tenders': 'Tenders',
      '/admin/jobs': 'Jobs',
      '/admin/applications': 'Applications',
      '/admin/events': 'Events',
      '/admin/announcement': 'Announcement',
      '/admin/members': 'Leadership',
      '/admin/member-countries': 'Members Country',
      '/admin/history': 'History',
      '/admin/my-profile': 'Profile',
    }
    return titleMap[pathname] || 'Admin Panel'
  }

  const getTimeOfDay = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Morning'
    if (hour < 18) return 'Afternoon'
    return 'Evening'
  }

  const getHeaderDescription = () => {
    const HEADER_DESCRIPTIONS: Record<string, string> = {
  '/admin/dashboard': 'Manage and grow your website content effectively',
  '/admin/content': 'Manage and edit website content',
  "/admin/members": "Manage published and draft leadership profiles",
  "/admin/member-countries": "Manage member countries and regional groupings",
  "/admin/press-release": "Manage press releases, drafts and publishing workflow",
  "/admin/strategic-pillars": "Manage strategic pillars, drafts and publishing workflow",
  "/admin/publications": "Manage research papers, reports and downloadable resources",
  "/admin/legal-foundations": "Manage legal foundations, reports and downloadable resources",
  "/admin/history": "Manage published histories, stories and drafts",
  "/admin/documents": "Manage and publish procurement related documents",
  "/admin/jobs": "Manage, publish and track job postings",
  "/admin/applications": "Track, review and manage candidates across all job posts",
  "/admin/events": "Manage, publish and organize events",
  "/admin/photo-gallery": "Organize folders, albums and visual assets for the website",
  "/admin/videos": "Manage video content, highlights and multimedia assets",
  "/admin/admin-users": "Invite, manage and monitor team members who help run the platform",
  "/admin/admin-users/": "View and manage staff account details, roles, and access",
};

if (location.pathname.includes('/admin-users/')) {
  return HEADER_DESCRIPTIONS['/admin/admin-users/'];
}

return HEADER_DESCRIPTIONS[location.pathname] || '';
  }

  const getPublicUrl = () => {
    const baseUrl = import.meta.env.VITE_WEBSITE_PUBLIC_URL || 'https://africanspaceagency.org';  
    const pathname = location.pathname

    // Helper to extract ID from admin path, handling sub-routes
    // Matches /admin/<module>/<id>(/...)
    const extractId = (module: string) => {
      const regex = new RegExp(`/admin/${module}/([^/]+)(?:/|$)`);
      const match = pathname.match(regex);
      // Filter out non-ID reserved words
      if (match && match[1] && !['new', 'add', 'edit', 'upload'].includes(match[1])) {
        return match[1];
      }
      return null;
    };

    // Dynamic ID-based routes
    // Press Release
    const pressReleaseId = extractId('press-release');
    if (pressReleaseId) return `${baseUrl}/media/press-release/${pressReleaseId}`;

    // Strategic Pillars
    const strategicPillarId = extractId('strategic-pillars');
    if (strategicPillarId) return `${baseUrl}/programmes/strategic-pillars/${strategicPillarId}`;

    // Programmes
    const programmeId = extractId('programmes');
    if (programmeId) return `${baseUrl}/programmes/${programmeId}`;

    // Events
    const eventId = extractId('events');
    if (eventId) return `${baseUrl}/events/${eventId}`;

    // Jobs
    const jobId = extractId('jobs');
    if (jobId) return `${baseUrl}/opportunities/careers/${jobId}`;

    // Tenders
    const tenderId = extractId('tenders');
    if (tenderId) return `${baseUrl}/opportunities/${tenderId}`;

    // Documents
    const documentId = extractId('documents');
    if (documentId) return `${baseUrl}/opportunities/${documentId}`;

    // Member Countries
    const memberCountryId = extractId('member-countries');
    if (memberCountryId) return `${baseUrl}/about/member-countries/${memberCountryId}`;

    // Members (Leadership)
    const memberId = extractId('members');
    if (memberId) return `${baseUrl}/about/leadership/${memberId}/biography`;

    // Map admin routes to public routes (Static / List pages)
    const routeMap: Record<string, string> = {
      '/admin/press-release': '/media/press-release',
      '/admin/strategic-pillars': '/programmes/strategic-pillars',
      '/admin/programmes': '/programmes/programs',
      '/admin/publications': '/resources',
      '/admin/photo-gallery': '/media/photo-gallery',
      '/admin/videos': '/media/videos',
      '/admin/events': '/events',
      '/admin/jobs': '/opportunities/careers/jobs',
      '/admin/tenders': '/opportunities',
      '/admin/documents': '/opportunities',
      '/admin/members': '/about/leadership',
      '/admin/member-countries': '/about/member-countries',
      '/admin/history': '/about/history',
      '/admin/about': '/about/who-we-are',
      '/admin/content': '/',
      '/admin/dashboard': '/',
    }

    // Check for exact matches first
    if (routeMap[pathname]) {
      return `${baseUrl}${routeMap[pathname]}`
    }

    // Check for partial matches (for sub-routes of list pages that don't have IDs)
    for (const [adminRoute, publicRoute] of Object.entries(routeMap)) {
      if (pathname.startsWith(adminRoute)) {
        return `${baseUrl}${publicRoute}`
      }
    }

    // Default to home page
    return baseUrl
  }

  const shouldShowViewWebsite = () => {
    const pathname = location.pathname

    // Pages that should NOT show "View Website" button
    const excludedRoutes = [
      '/admin/admin-users',
      '/admin/my-profile',
      '/admin/subscribers',
      '/admin/announcement',
    ]

    // Check if current path is in excluded routes
    return !excludedRoutes.some(route => pathname.startsWith(route))
  }

  const handleCreateNew = (articleType: 'Press Release' | 'Strategic Pillars' | 'Programmes') => {
    setArticleModalOpen(false)

    // Map article types to routes
    const routeMap: Record<string, string> = {
      'Press Release': '/admin/press-release/new',
      'Strategic Pillars': '/admin/strategic-pillars/new',
      'Programmes': '/admin/programmes/new',
    }

    const targetRoute = routeMap[articleType]
    if (targetRoute) {
      navigate(targetRoute)
    }
  }

  // Check if we're in editor mode via route pathname
  const pathname = location.pathname
  const isPressReleaseEditor = pathname.includes('/press-release/') && (pathname.includes('/new') || pathname.includes('/edit'))
  const isStrategicPillarEditor = pathname.includes('/strategic-pillars/') && (pathname.includes('/new') || pathname.includes('/edit'))
  const isProgrammeEditor = pathname.includes('/programmes/') && (pathname.includes('/new') || pathname.includes('/edit'))
  const isPublicationEditor = pathname.includes('/publications/') && (pathname.includes('/new') || pathname.includes('/edit'))
  const isPhotoUploader = pathname.includes('/photo-gallery/') && (pathname.includes('/upload') || pathname.includes('/edit/'))
  const isMemberCountryEditor = pathname.includes('/member-countries/') && (pathname.includes('/add') || pathname.includes('/edit/'))
  const isMemberEditor = pathname.includes('/members/') && (pathname.includes('/add') || pathname.includes('/edit/'))
  const isHistoryEditor = pathname.includes('/history/') && (pathname.includes('/add') || pathname.includes('/edit/'))
  const isDocumentEditor = pathname.includes('/documents/') && (pathname.includes('/add') || pathname.includes('/edit/'))
  const isJobEditor = pathname.includes('/jobs/') && (pathname.includes('/add') || pathname.includes('/edit/'))
  const isTenderEditor = pathname.includes('/tenders/') && (pathname.includes('/add') || pathname.includes('/edit/'))
  const isEventEditor = pathname.includes('/events/') && (pathname.includes('/add') || pathname.includes('/edit/'))
  const isEditorMode = isPressReleaseEditor || isStrategicPillarEditor || isProgrammeEditor || isPublicationEditor || isPhotoUploader || isMemberCountryEditor || isMemberEditor || isHistoryEditor || isDocumentEditor || isJobEditor || isTenderEditor || isEventEditor

  // Don't show header in editor mode
  if (isEditorMode) {
    return (
      <div className="flex h-screen bg-white font-sans">
            <Sidebar
        openArticleModal={() => setArticleModalOpen(true)}
      />
         <main className="flex-1 p-4 xl:p-6 gap-4 lg:gap-6 xl:gap-8 flex flex-col overflow-y-auto">
        <div className="h-full flex-1 overflow-y-auto">
          {children}
        </div>
        </main>
        <ArticleTypeModal
          isOpen={isArticleModalOpen}
          onClose={() => setArticleModalOpen(false)}
          onSelect={handleCreateNew}
        />
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-white font-sans">
      <Sidebar
        openArticleModal={() => setArticleModalOpen(true)}
      />
      <main className="flex-1 p-4 xl:p-6 gap-4 lg:gap-6 xl:gap-8 flex flex-col overflow-y-auto">
        {!hideHeader && <header className="bg-white  flex justify-between items-center rounded-lg">
          <div>
            <h1 className="text-xl font-medium text-gray-800">{getPageTitle()}</h1>
            {getHeaderDescription() && <p className="text-sm text-gray-500">{getHeaderDescription()}</p>}
          </div>
          {shouldShowViewWebsite() && (
            <a
              href={getPublicUrl()}
              className="flex items-center gap-2 text-sm font-semibold text-gray-700 bg-white  rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Website
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.6062 9.035V16.964C16.6084 17.266 16.5502 17.5654 16.4352 17.8446C16.3202 18.1238 16.1507 18.3773 15.9365 18.5902C15.7223 18.8031 15.4678 18.9711 15.1878 19.0844C14.9079 19.1977 14.6082 19.254 14.3062 19.25H3.0492C2.74613 19.2539 2.44534 19.1971 2.16459 19.0828C1.88383 18.9686 1.6288 18.7993 1.41452 18.585C1.20024 18.3706 1.03107 18.1155 0.916971 17.8347C0.802875 17.5539 0.74617 17.2531 0.750201 16.95V5.693C0.746579 5.38996 0.803597 5.08925 0.917901 4.80856C1.0322 4.52788 1.20148 4.27288 1.41578 4.05858C1.63009 3.84428 1.88508 3.675 2.16576 3.5607C2.44645 3.4464 2.74716 3.38938 3.0502 3.393H10.9782M19.2492 0.75L8.6782 11.321M19.2492 0.75H13.9632M19.2492 0.75V6.036" stroke="#00764B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          )}
        </header>}
        <div className={`flex-1  ${hideHeader ? 'p-0' : ''}`}>
          {children}
        </div>
      </main>
      <ArticleTypeModal
        isOpen={isArticleModalOpen}
        onClose={() => setArticleModalOpen(false)}
        onSelect={handleCreateNew}
      />
    </div>
  )
}

export default AdminLayout